/** Безопасный парсер математических выражений (без eval). */

export type AngleMode = "deg" | "rad";

export type EvalOptions = {
  angleMode?: AngleMode;
  /** Значение переменной x (для графиков). */
  x?: number;
};

const MAX_EXPR_LEN = 256;

export function formatMathNumber(n: number, maxLen = 16): string {
  if (!Number.isFinite(n)) return "Error";
  const abs = Math.abs(n);
  let s: string;
  if (abs !== 0 && (abs >= 1e12 || abs < 1e-8)) {
    s = n.toExponential(8).replace(/\.?0+e/, "e").replace(/e\+?/, "e");
  } else {
    s = String(Number(n.toPrecision(12)));
  }
  if (s.length > maxLen) {
    s = n.toExponential(6).replace(/\.?0+e/, "e").replace(/e\+?/, "e");
  }
  return s.length > maxLen ? "Error" : s;
}

function toRad(v: number, mode: AngleMode): number {
  return mode === "deg" ? (v * Math.PI) / 180 : v;
}

function fromRad(v: number, mode: AngleMode): number {
  return mode === "deg" ? (v * 180) / Math.PI : v;
}

/**
 * Градусы → «градусы.минуты-секунды», кнопка dms инженерного калькулятора.
 *
 * 1,5° → 1,3 (это 1°30'00"): минуты занимают два знака после запятой,
 * секунды — следующие два.
 */
function toDms(v: number): number {
  if (!Number.isFinite(v)) return NaN;
  const sign = v < 0 ? -1 : 1;
  const a = Math.abs(v);
  const d = Math.floor(a);
  const mFull = (a - d) * 60;
  const m = Math.floor(mFull);
  const s = (mFull - m) * 60;
  return sign * (d + m / 100 + s / 10000);
}

/** «Градусы.минуты-секунды» → градусы, кнопка deg. Обратна toDms. */
function fromDms(v: number): number {
  if (!Number.isFinite(v)) return NaN;
  const sign = v < 0 ? -1 : 1;
  const a = Math.abs(v);
  const d = Math.floor(a);
  const rest = (a - d) * 100;
  const m = Math.floor(rest + 1e-9);
  const s = (rest - m) * 100;
  return sign * (d + m / 60 + s / 3600);
}

function factorial(n: number): number {
  if (!Number.isFinite(n) || n < 0 || n !== Math.floor(n) || n > 170) return NaN;
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
}

type Tok =
  | { kind: "num"; value: number }
  | { kind: "id"; value: string }
  | { kind: "op"; value: string }
  | { kind: "lparen" }
  | { kind: "rparen" }
  | { kind: "comma" };

function tokenize(input: string): Tok[] {
  const s = input.replace(/\s+/g, "").replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-");
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    if ((c >= "0" && c <= "9") || c === ".") {
      let j = i + 1;
      while (j < s.length && ((s[j]! >= "0" && s[j]! <= "9") || s[j] === ".")) j++;
      if (j < s.length && (s[j] === "e" || s[j] === "E")) {
        let k = j + 1;
        if (k < s.length && (s[k] === "+" || s[k] === "-")) k++;
        while (k < s.length && s[k]! >= "0" && s[k]! <= "9") k++;
        j = k;
      }
      const num = Number(s.slice(i, j));
      if (!Number.isFinite(num)) throw new Error("bad number");
      out.push({ kind: "num", value: num });
      i = j;
      continue;
    }
    if ((c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || c === "π") {
      if (c === "π") {
        out.push({ kind: "id", value: "pi" });
        i++;
        continue;
      }
      // infix mod: 10mod3 → mod + 3 (не склеивать цифры в идентификатор)
      if (s.slice(i, i + 3).toLowerCase() === "mod") {
        const next = s[i + 3];
        if (!next || !/[a-zA-Z_]/.test(next)) {
          out.push({ kind: "id", value: "mod" });
          i += 3;
          continue;
        }
      }
      let j = i + 1;
      while (j < s.length && /[a-zA-Z0-9_]/.test(s[j]!)) j++;
      out.push({ kind: "id", value: s.slice(i, j).toLowerCase() });
      i = j;
      continue;
    }
    if (c === "(") {
      out.push({ kind: "lparen" });
      i++;
      continue;
    }
    if (c === ")") {
      out.push({ kind: "rparen" });
      i++;
      continue;
    }
    if (c === ",") {
      out.push({ kind: "comma" });
      i++;
      continue;
    }
    if ("+-*/^%!".includes(c)) {
      out.push({ kind: "op", value: c });
      i++;
      continue;
    }
    throw new Error(`bad char ${c}`);
  }
  return out;
}

type Parser = {
  toks: Tok[];
  i: number;
  angleMode: AngleMode;
  x: number | undefined;
};

function peek(p: Parser): Tok | undefined {
  return p.toks[p.i];
}

function take(p: Parser): Tok | undefined {
  return p.toks[p.i++];
}

function parseExpr(p: Parser): number {
  return parseAdd(p);
}

function parseAdd(p: Parser): number {
  let v = parseMul(p);
  for (;;) {
    const t = peek(p);
    if (t?.kind === "op" && (t.value === "+" || t.value === "-")) {
      take(p);
      const r = parseMul(p);
      v = t.value === "+" ? v + r : v - r;
    } else break;
  }
  return v;
}

function parseMul(p: Parser): number {
  let v = parsePow(p);
  for (;;) {
    const t = peek(p);
    if (t?.kind === "op" && (t.value === "*" || t.value === "/")) {
      take(p);
      const r = parsePow(p);
      v = t.value === "*" ? v * r : r === 0 ? NaN : v / r;
    } else if (t?.kind === "id" && t.value === "mod") {
      take(p);
      const r = parsePow(p);
      v = r === 0 ? NaN : v % r;
    } else if (
      t &&
      (t.kind === "num" ||
        t.kind === "id" ||
        t.kind === "lparen" ||
        (t.kind === "op" && t.value === "!"))
    ) {
      // implicit multiply: 2pi, 2(3), 2sin(...)
      if (t.kind === "op" && t.value === "!") break;
      const r = parsePow(p);
      v = v * r;
    } else break;
  }
  return v;
}

function parsePow(p: Parser): number {
  let v = parseUnary(p);
  const t = peek(p);
  if (t?.kind === "op" && t.value === "^") {
    take(p);
    const r = parsePow(p); // right-assoc
    v = Math.pow(v, r);
  }
  // postfix !
  for (;;) {
    const t2 = peek(p);
    if (t2?.kind !== "op" || t2.value !== "!") break;
    take(p);
    v = factorial(v);
  }
  return v;
}

function parseUnary(p: Parser): number {
  const t = peek(p);
  if (t?.kind === "op" && t.value === "+") {
    take(p);
    return parseUnary(p);
  }
  if (t?.kind === "op" && t.value === "-") {
    take(p);
    return -parseUnary(p);
  }
  if (t?.kind === "op" && t.value === "%") {
    take(p);
    return parseUnary(p) / 100;
  }
  return parsePrimary(p);
}

function callFn(name: string, args: number[], mode: AngleMode): number {
  const a = args[0] ?? NaN;
  switch (name) {
    case "sin":
      return Math.sin(toRad(a, mode));
    case "cos":
      return Math.cos(toRad(a, mode));
    case "tan":
      return Math.tan(toRad(a, mode));
    case "cot": {
      const t = Math.tan(toRad(a, mode));
      return t === 0 ? NaN : 1 / t;
    }
    case "asin":
    case "arcsin":
      return fromRad(Math.asin(a), mode);
    case "acos":
    case "arccos":
      return fromRad(Math.acos(a), mode);
    case "atan":
    case "arctan":
      return fromRad(Math.atan(a), mode);
    case "acot":
    case "arccot":
      return fromRad(Math.atan(a === 0 ? Infinity : 1 / a), mode);
    case "sqrt":
      return Math.sqrt(a);
    case "cbrt":
      return Math.cbrt(a);
    case "abs":
      return Math.abs(a);
    case "ln":
      return Math.log(a);
    case "log":
      return args.length >= 2 ? Math.log(args[1]!) / Math.log(a) : Math.log10(a);
    case "log10":
      return Math.log10(a);
    case "exp":
      return Math.exp(a);
    case "fact":
    case "factorial":
      return factorial(a);
    case "sec": {
      const c = Math.cos(toRad(a, mode));
      return c === 0 ? NaN : 1 / c;
    }
    case "csc": {
      const s = Math.sin(toRad(a, mode));
      return s === 0 ? NaN : 1 / s;
    }
    case "asec":
      return a === 0 ? NaN : fromRad(Math.acos(1 / a), mode);
    case "acsc":
      return a === 0 ? NaN : fromRad(Math.asin(1 / a), mode);
    // Гиперболические не зависят от градусов/радиан — как в Windows.
    case "sinh":
      return Math.sinh(a);
    case "cosh":
      return Math.cosh(a);
    case "tanh":
      return Math.tanh(a);
    case "coth": {
      const t = Math.tanh(a);
      return t === 0 ? NaN : 1 / t;
    }
    case "sech": {
      const c = Math.cosh(a);
      return c === 0 ? NaN : 1 / c;
    }
    case "csch": {
      const s = Math.sinh(a);
      return s === 0 ? NaN : 1 / s;
    }
    case "asinh":
      return Math.asinh(a);
    case "acosh":
      return Math.acosh(a);
    case "atanh":
      return Math.atanh(a);
    case "acoth":
      return a === 0 ? NaN : Math.atanh(1 / a);
    case "asech":
      return a === 0 ? NaN : Math.acosh(1 / a);
    case "acsch":
      return a === 0 ? NaN : Math.asinh(1 / a);
    case "floor":
      return Math.floor(a);
    case "ceil":
      return Math.ceil(a);
    case "dms":
      return toDms(a);
    case "degs":
      return fromDms(a);
    case "pow":
      return Math.pow(a, args[1] ?? NaN);
    default:
      return NaN;
  }
}

function parsePrimary(p: Parser): number {
  const t = take(p);
  if (!t) throw new Error("unexpected end");
  if (t.kind === "num") return t.value;
  if (t.kind === "id") {
    if (t.value === "pi" || t.value === "π") return Math.PI;
    if (t.value === "e") return Math.E;
    if (t.value === "x") {
      if (p.x === undefined || !Number.isFinite(p.x)) throw new Error("no x");
      return p.x;
    }
    // function call
    if (peek(p)?.kind === "lparen") {
      take(p);
      const args: number[] = [];
      if (peek(p)?.kind !== "rparen") {
        args.push(parseExpr(p));
        while (peek(p)?.kind === "comma") {
          take(p);
          args.push(parseExpr(p));
        }
      }
      if (peek(p)?.kind !== "rparen") throw new Error("missing )");
      take(p);
      return callFn(t.value, args, p.angleMode);
    }
    throw new Error(`unknown id ${t.value}`);
  }
  if (t.kind === "lparen") {
    const v = parseExpr(p);
    if (peek(p)?.kind !== "rparen") throw new Error("missing )");
    take(p);
    return v;
  }
  throw new Error("bad primary");
}

/** Вычислить выражение. Возвращает число или NaN при ошибке. */
export function evalMath(expr: string, opts: EvalOptions = {}): number {
  const raw = expr.trim();
  if (!raw || raw.length > MAX_EXPR_LEN) return NaN;
  try {
    const toks = tokenize(raw);
    if (!toks.length) return NaN;
    const p: Parser = {
      toks,
      i: 0,
      angleMode: opts.angleMode ?? "deg",
      x: opts.x,
    };
    const v = parseExpr(p);
    if (p.i !== p.toks.length) return NaN;
    return Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}

/** Компиляция f(x) для графика. */
export function compileFn(
  expr: string,
  angleMode: AngleMode = "deg"
): (x: number) => number {
  const trimmed = expr.trim() || "0";
  return (x: number) => evalMath(trimmed, { angleMode, x });
}
